import { motion } from "framer-motion";
import { useMemo } from "react";
import { MapPin, TrendingDown, TrendingUp } from "lucide-react";
import type { GasStation } from "@/types/database";
import {
  FUEL_PRICE_LABELS,
  getFuelNetworkCharts,
  getNetworkOverview,
  type FuelPricePoint,
} from "@/lib/analytics";
import { LazyFuelNetworkCharts } from "@/components/gas-stations/FuelNetworkChartsClient";
import { StationBrandMark } from "@/components/gas-stations/StationBrandMark";
import { Card, MetricCard } from "@/components/ui/Card";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { formatFuelPrice, formatNumber } from "@/lib/utils";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  },
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-wider text-muted">{children}</p>
  );
}

function FuelExtremePanel({
  kind,
  point,
}: {
  kind: "highest" | "lowest";
  point: FuelPricePoint | null;
}) {
  const isHigh = kind === "highest";
  const Icon = isHigh ? TrendingUp : TrendingDown;

  return (
    <Card className="p-4" flat>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={
              isHigh
                ? "flex h-8 w-8 items-center justify-center rounded-full bg-[#fff4e5] text-fuel-high"
                : "flex h-8 w-8 items-center justify-center rounded-full bg-accent-subtle text-fuel-low"
            }
          >
            <Icon className="h-4 w-4" strokeWidth={2.25} />
          </span>
          <div>
            <SectionLabel>
              {isHigh ? "Highest" : "Lowest"} {FUEL_PRICE_LABELS.fuelPrice_87}
            </SectionLabel>
            {point ? (
              <p className="mt-1 text-xl font-extrabold tracking-tight">{formatFuelPrice(point.price)}</p>
            ) : (
              <p className="mt-1 text-sm text-muted">No data</p>
            )}
          </div>
        </div>
        {point ? <StationBrandMark station={point.station} size={28} /> : null}
      </div>
      {point ? (
        <>
          <p className="mt-3 truncate text-sm font-bold">{point.station.name}</p>
          <p className="mt-0.5 flex items-start gap-1 text-xs text-muted">
            <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
            <span className="line-clamp-1">{point.station.parish || point.station.address}</span>
          </p>
        </>
      ) : null}
    </Card>
  );
}

function FuelPriceRangeBar({
  min,
  max,
  average,
}: {
  min: number;
  max: number;
  average: number;
}) {
  const span = max - min || 1;
  const avgPct = ((average - min) / span) * 100;

  return (
    <div className="space-y-3">
      <SectionLabel>E-10 87 price range</SectionLabel>
      <div className="relative pt-1">
        <div className="h-2.5 overflow-hidden rounded-full bg-input">
          <motion.div
            className="h-full w-full rounded-full bg-gradient-to-r from-[#34c759] via-[#fff200] to-[#ff9500]"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "left center" }}
          />
        </div>
        <motion.div
          className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-foreground shadow-sm"
          initial={{ left: "0%" }}
          animate={{ left: `${Math.min(Math.max(avgPct, 3), 97)}%` }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden
        />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Low</p>
          <p className="mt-0.5 font-extrabold">{formatFuelPrice(min)}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Average</p>
          <p className="mt-0.5 font-extrabold">{formatFuelPrice(average)}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted">High</p>
          <p className="mt-0.5 font-extrabold">{formatFuelPrice(max)}</p>
        </div>
      </div>
    </div>
  );
}

export function FuelNetworkAnalytics({ stations }: { stations: GasStation[] }) {
  const overview = useMemo(() => getNetworkOverview([], stations), [stations]);
  const charts = useMemo(() => getFuelNetworkCharts(stations), [stations]);
  const brandCount = useMemo(
    () => new Set(stations.map((station) => station.name.trim()).filter(Boolean)).size,
    [stations]
  );

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-4">
      <motion.div variants={itemVariants}>
        <Workspace>
          <WorkspaceSection className="p-4 sm:p-5">
            <div className="mb-5">
              <h2 className="font-bold text-foreground">Fuel insights</h2>
              <p className="mt-1 text-sm text-muted">
                Live pricing comparisons across brands, parishes, and fuel grades.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard label="Stations" value={formatNumber(overview.gasStationCount)} className="p-4" />
              <MetricCard label="Avg E-10 87" value={formatFuelPrice(overview.avgFuel87)} className="p-4" />
              <MetricCard label="Price spread" value={formatFuelPrice(overview.spread)} className="p-4" />
              <MetricCard label="Fuel brands" value={formatNumber(brandCount)} className="p-4" />
            </div>
          </WorkspaceSection>

          <WorkspaceSection border className="grid gap-4 p-4 sm:p-5 lg:grid-cols-2">
            <FuelExtremePanel kind="lowest" point={overview.fuel87.lowest} />
            <FuelExtremePanel kind="highest" point={overview.fuel87.highest} />
          </WorkspaceSection>

          {overview.fuel87.highest && overview.fuel87.lowest ? (
            <WorkspaceSection border className="p-4 sm:p-5">
              <FuelPriceRangeBar
                min={overview.fuel87.lowest.price}
                max={overview.fuel87.highest.price}
                average={overview.avgFuel87}
              />
            </WorkspaceSection>
          ) : null}

          <WorkspaceSection border className="p-4 sm:p-5">
            <LazyFuelNetworkCharts charts={charts} />
          </WorkspaceSection>
        </Workspace>
      </motion.div>
    </motion.div>
  );
}
